"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Send } from "lucide-react";

type AudioRecorderProps = {
  onSubmit?: (blob: Blob) => void | Promise<void>;
  disabled?: boolean;
  submitLabel?: string;
};

export default function AudioRecorder({
  onSubmit,
  disabled = false,
  submitLabel = "Enviar",
}: AudioRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSending, setIsSending] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let animationId: number = 0;

    const drawWaveform = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 2);

      for (let i = 0; i < canvas.width; i++) {
        const y = isRecording
          ? canvas.height / 2 + (Math.random() - 0.5) * 20
          : canvas.height / 2;
        ctx.lineTo(i, y);
      }

      ctx.strokeStyle = "#9CA3AF";
      ctx.stroke();

      animationId = requestAnimationFrame(drawWaveform);
    };

    if (isRecording) {
      drawWaveform();
    } else {
      cancelAnimationFrame(animationId);
    }

    return () => cancelAnimationFrame(animationId);
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      mediaRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorderRef.current.onstop = () => {
        setAudioBlob(new Blob(chunks, { type: "audio/webm" }));
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Error accessing microphone:", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const toggleRecording = () => {
    if (disabled || isSending) return;
    if (isRecording) {
      stopRecording();
    } else {
      setAudioBlob(null);
      void startRecording();
    }
  };

  const sendAudio = async () => {
    if (!audioBlob || !onSubmit || disabled || isSending) return;
    setIsSending(true);
    try {
      await onSubmit(audioBlob);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-gray-100 rounded-full p-2 flex items-center mb-2">
        <canvas
          ref={canvasRef}
          className="flex-grow h-8"
          width={300}
          height={32}
        />
        <Button
          onClick={toggleRecording}
          disabled={disabled || isSending}
          type="button"
          size="icon"
          variant="ghost"
          aria-label={isRecording ? "Parar gravação" : "Gravar"}
        >
          <Mic className={isRecording ? "text-red-500" : undefined} />
        </Button>
      </div>
      <Button
        onClick={() => void sendAudio()}
        disabled={!audioBlob || disabled || isSending || !onSubmit}
        className="w-full"
        type="button"
      >
        <Send className="mr-2 h-4 w-4" />
        {isSending ? "Enviando…" : submitLabel}
      </Button>
    </div>
  );
}
